import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { EditableDBItemV3, ModuleFE_MouseInteractivity, openContent } from '@nu-art/thunderstorm-frontend/index';
import { filterInstances, MissingDataException, MUSTNeverHappenException, ThisShouldNotHappenException } from '@nu-art/ts-common';
import { Empty_PathwayState } from '../consts.js';
import { Header_WithOptionMenu } from '@app/styles/frontend/ui-manager/headers/Header_WithOptionMenu/index';
import { Dialog_Confirmation } from '@app/styles/frontend/ui-manager/dialogs/ConfirmationDialog/index';
import { ModuleFE_Tag, ModuleFE_Variable } from '@app/hcs-frontend';
import { ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition, } from '../../../_entity.js';
import { ICONS } from '@app/styles';
import { AccessLevel, ModuleFE_PermissionsAssert } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { Dialog_DuplicatePathway } from '../../_dialogs/Dialog_DuplicatePathway/Dialog_DuplicatePathway.js';
import { Dialog_CopyTransition } from '../selectors/renderers/renderers-pathway-transition/CopyTransitionDialog/Dialog_CopyTransition.js';
import { pathwayType_SingleState, PathwayTypeToLabel } from '@app/pathway-shared';
import { CreateDiscussionPopup } from '@app/hcs-frontend/messaging';
export const Header_Pathway = (props) => {
    const pathway = props.pathway.item;
    const pathwayVariableNames = pathway.variableIds?.map(id => {
        const variable = ModuleFE_Variable.cache.unique(id);
        if (!variable)
            throw new MissingDataException(`pathway variable with id ${id} not found`);
        return variable.name;
    });
    const titleClickAction = props.titleClickAction ?? (() => props.functions.deselectPathwayState());
    const isArchived = pathway.archived ?? false;
    const archivedStatusChange = isArchived ? 'Unarchive' : 'Archive';
    const archiveTitle = archivedStatusChange + ' Pathway';
    const tagGroups = pathway.tagGroups;
    const selectedTagsTitle = tagGroups?.map((tagGroup, groupIndex) => {
        return (_jsxs(React.Fragment, { children: [tagGroup.value.map((childTagId, index) => {
                    const tagGroupChild = ModuleFE_Tag.cache.unique(childTagId);
                    if (!tagGroupChild)
                        throw new MUSTNeverHappenException(`cannot find tag group with id ${childTagId}`);
                    return tagGroupChild.label;
                }).join(' | '), _jsx("br", {})] }, groupIndex));
    }).flat();
    const pasteTransition = async () => {
        const pathwayTransitionToCopy = (await ModuleFE_PathwayTransition.getCopiedTransition());
        if (!pathwayTransitionToCopy || !pathway)
            return;
        Dialog_CopyTransition.show({ transitionToCopy: pathwayTransitionToCopy.data, pathwayId: pathway._id });
    };
    const subtitle = () => {
        const pathway = props.pathway.item;
        return _jsxs("div", { style: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, fontSize: '12px' }, children: [`${PathwayTypeToLabel[pathway.type]} Pathway | ${pathwayVariableNames?.join(' - ')}`, pathway._parentId && _jsxs(_Fragment, { children: [" | Child of ", _jsx("span", { style: { paddingLeft: '3px' }, onClick: () => props.functions.selectPathway(pathway._parentId), className: 'clickable', children: ModuleFE_Pathway.cache.unique(pathway._parentId)?.label ?? 'not found' })] }), _jsx("br", {}), selectedTagsTitle] }, pathway._id);
    };
    const canDeletePathway = ModuleFE_PermissionsAssert.getAccessLevel(ModuleFE_Pathway.permissions.collection.delete) === AccessLevel.HasAccess;
    return _jsx(Header_WithOptionMenu, { title: pathway.label, iconToRender: pathway.underConstruction &&
            _jsx(ICONS.under_construction.component, { ...openContent.tooltip.top('under-construction', _jsx(_Fragment, { children: "Protocol under construction" }), { offset: 5 }) }), subtitle: subtitle, className: 'underline', asyncTitleClick: props.isClickAsync, onTitleClick: props.clickableTitle ? titleClickAction : undefined, menuActions: !props.allowActions ? undefined : filterInstances([
            pathway?._id ? {
                label: 'Add Note', action: (e) => {
                    const discussionView = {
                        id: 'discussion-right-click-menu',
                        content: _jsx(CreateDiscussionPopup, { entityName: ModuleFE_Pathway.dbDef.entityName, refId: pathway._id }),
                        originPos: {
                            y: e.clientY,
                            x: e.clientX,
                        },
                        modalPos: { x: 0, y: 1 },
                    };
                    try {
                        ModuleFE_MouseInteractivity.showContent(discussionView);
                    }
                    catch (e) {
                        console.log(e);
                    }
                }
            } : undefined,
            { label: 'Edit Pathway', action: props.functions.openPathwayEdit },
            { label: 'Paste Transition', action: pasteTransition },
            canDeletePathway ? {
                label: 'Delete Pathway',
                action: (e) => {
                    Dialog_Confirmation.show({
                        header: 'Are you sure?',
                        content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to delete a pathway" }), _jsx("div", { children: pathway.label })] }),
                        buttons: [
                            {
                                label: 'Yes, Delete',
                                isAsync: true,
                                action: async () => {
                                    const pathwayId = pathway._id;
                                    if (!pathwayId)
                                        throw new MUSTNeverHappenException('Trying to delete a pathway without an _id');
                                    await ModuleFE_Pathway._v1.deletePathway({ pathwayId }).executeSync();
                                    props.functions.deselectPathway();
                                }
                            },
                            {
                                label: 'Don\'t Delete',
                                isPreferred: true,
                                action: () => {
                                }
                            }
                        ]
                    });
                }
            } : undefined,
            {
                label: 'Create Child Pathway',
                action: (e) => {
                    Dialog_Confirmation.show({
                        header: 'Create Child Pathway',
                        content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: "You're about to create a child pathway from:" }), _jsx("div", { children: pathway.label })] }),
                        buttons: [
                            {
                                label: 'Yes, Create',
                                isAsync: true,
                                action: async () => {
                                    const pathwayId = props.pathway.item._id;
                                    if (!pathwayId)
                                        throw new MUSTNeverHappenException('Trying to create a child from pathway without an _id');
                                    const { pathway } = await ModuleFE_Pathway._v1.createChildPathway({ pathwayId }).executeSync();
                                    props.functions.selectPathway(pathway._id);
                                }
                            },
                            {
                                label: 'Cancel',
                                isPreferred: true,
                                action: () => {
                                }
                            }
                        ]
                    });
                }
            },
            (pathway?._id) ? {
                label: 'Duplicate Pathway',
                action: (e) => {
                    Dialog_DuplicatePathway.show({
                        pathway: props.pathway.item,
                        onPathwayDuplicated: (newPathway) => props.functions.selectPathway(newPathway._id),
                    });
                }
            } : undefined,
            {
                label: archiveTitle,
                action: (e) => {
                    Dialog_Confirmation.show({
                        header: archiveTitle,
                        content: () => _jsxs(_Fragment, { children: [_jsx("div", { children: `You're about to ${archivedStatusChange} a pathway` }), _jsx("div", { children: props.pathway.item.label })] }),
                        buttons: [
                            {
                                label: `Yes, ${archivedStatusChange}`,
                                isAsync: true,
                                action: async () => {
                                    const pathwayId = props.pathway.item._id;
                                    if (!pathwayId)
                                        throw new ThisShouldNotHappenException(`Could not ${archivedStatusChange} pathway`);
                                    await ModuleFE_Pathway._v1.togglePathwayArchiveStatus({ pathwayId }).executeSync();
                                    props.functions.deselectPathway();
                                }
                            },
                            {
                                label: `Don\'t ${archivedStatusChange}`,
                                isPreferred: true,
                                action: () => {
                                }
                            }
                        ]
                    });
                }
            },
            props.pathway.item.type === pathwayType_SingleState ? undefined : {
                label: 'Add New State',
                action: (e) => {
                    const pathwayId = props.pathway.item._id;
                    if (!pathwayId)
                        throw new MUSTNeverHappenException('Trying to set a new state for pathway with no id');
                    const newState = Empty_PathwayState(pathwayId);
                    props.functions.selectPathwayState(new EditableDBItemV3(newState, ModuleFE_PathwayState), true);
                }
            }
        ]) }, pathway._id);
};
//# sourceMappingURL=Header_Pathway.js.map